import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import PaymentModal from './PaymentModal';
import TransactionHistory from './TransactionHistory';
import CommunicationLog from './CommunicationLog';
import ReviewModal from './ReviewModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiUser, FiDollarSign, FiBookOpen, FiReceipt, FiEye, FiClock, FiCheckCircle, FiArrowLeft, FiMessageSquare, FiClipboard, FiX, FiStar, FiCalendar, FiBarChart2 } = FiIcons;

const ParentPortal = () => {
  const { user, logout } = useAuth();
  const { students, parents, sessions, appSettings, formatCurrency, calculateRemainingBalance, studentReviews, tutorReviews, calculateTotalSessions, calculatePaidSessions, calculateUnpaidSessions } = useData();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [sessionForReview, setSessionForReview] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);

  const parentRecord = parents.find(p => p.id === user?.uid || p.id === user?.id || p.email?.toLowerCase() === user?.email?.toLowerCase());
  const myChildren = students.filter(student => student.parentId === parentRecord?.id);

  const ViewReportModal = ({ report, onClose }) => (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[110] backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[3rem] shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-900 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiClipboard} className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight">Session Report</h2>
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-400">Feedback from {report.tutorName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-all">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <div className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Subject</p>
              <p className="text-sm font-black text-gray-900">{report.subject}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Session Date</p>
              <p className="text-sm font-black text-gray-900">{new Date(report.sessionDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Tutor's Observation</p>
            <div className="bg-indigo-50/50 p-6 rounded-[2rem] border-2 border-indigo-50 relative">
              <SafeIcon icon={FiMessageSquare} className="absolute -top-3 -left-3 text-indigo-200 text-4xl -rotate-12" />
              <p className="text-gray-700 leading-relaxed font-bold italic relative z-10"> "{report.comment}" </p>
            </div>
          </div>
          <button onClick={onClose} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all"> Close Report </button>
        </div>
      </motion.div>
    </div>
  );

  const SessionList = ({ studentId }) => {
    const studentSessions = sessions[studentId] || {};
    const allSessions = Object.values(studentSessions)
      .flat()
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
      <div className="mt-12">
        <div className="flex items-center justify-between mb-6 px-2">
          <h3 className="text-xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-indigo-600" /> Class Log & Fees
          </h3>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-100 px-3 py-1 rounded-full">
            {allSessions.length} Total Sessions
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allSessions.map(s => {
            const report = studentReviews.find(r => r.sessionId === s.id);
            const parentRating = tutorReviews.find(r => r.sessionId === s.id);

            return (
              <motion.div whileHover={{ y: -5 }} key={s.id} className="bg-white border-2 border-gray-50 rounded-[2.5rem] p-6 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gray-50 rounded-2xl flex flex-col items-center justify-center text-gray-900 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    <span className="text-[9px] font-black uppercase leading-none mb-1">{new Date(s.date).toLocaleString('default', { month: 'short' })}</span>
                    <span className="text-xl font-black leading-none">{new Date(s.date).getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-black text-gray-900 truncate text-lg group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{s.subject}</h5>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 font-black uppercase tracking-widest">
                      <SafeIcon icon={FiUser} /> {s.tutor}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-4 border-t border-gray-50">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black text-gray-900">{formatCurrency(s.feePerSession)}</span>
                      <div className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest ${s.isPaid ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>
                        {s.isPaid ? 'Paid' : 'Due'}
                      </div>
                    </div>
                    {report && (
                      <button onClick={() => setViewingReport(report)} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all">
                        <SafeIcon icon={FiClipboard} /> Summary
                      </button>
                    )}
                  </div>

                  {/* Session-Wise Rating Button */}
                  <button 
                    onClick={() => {
                      setSessionForReview(s);
                      setShowReviewModal(true);
                    }}
                    className={`w-full py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
                      parentRating 
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                        : 'bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white shadow-sm'
                    }`}
                  >
                    <SafeIcon icon={parentRating ? FiCheckCircle : FiStar} />
                    {parentRating ? 'Class Rated' : 'Rate this Class'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: appSettings.brandColor }}>
                <SafeIcon icon={FiUser} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900 tracking-tight">{appSettings.companyName}</h1>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">Parent Portal</p>
              </div>
            </div>
            <button onClick={logout} className="flex items-center gap-2 px-5 py-2.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-all">
              <SafeIcon icon={FiLogOut} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-4 mb-10 overflow-x-auto pb-2 no-scrollbar">
          <button onClick={() => { setActiveTab('overview'); setSelectedStudent(null); }} className={`flex-shrink-0 flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'overview' ? 'bg-indigo-600 text-white shadow-xl' : 'bg-white text-gray-400 border border-gray-100'}`}>
            <SafeIcon icon={FiUser} /> Dashboard
          </button>
          <button onClick={() => { setActiveTab('transactions'); setSelectedStudent(null); }} className={`flex-shrink-0 flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'transactions' ? 'bg-indigo-600 text-white shadow-xl' : 'bg-white text-gray-400 border border-gray-100'}`}>
            <SafeIcon icon={FiReceipt} /> Payments
          </button>
          <button onClick={() => { setActiveTab('comms'); setSelectedStudent(null); }} className={`flex-shrink-0 flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'comms' ? 'bg-indigo-600 text-white shadow-xl' : 'bg-white text-gray-400 border border-gray-100'}`}>
            <SafeIcon icon={FiMessageSquare} /> Communication
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {!selectedStudent ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {myChildren.map(child => {
                  const unpaidCount = calculateUnpaidSessions(child.id);
                  const balance = calculateRemainingBalance(child.id);
                  return (
                    <motion.div whileHover={{ y: -8 }} key={child.id} onClick={() => setSelectedStudent(child)} className="bg-white p-8 rounded-[3rem] shadow-sm hover:shadow-2xl transition-all group cursor-pointer border border-transparent hover:border-indigo-100 overflow-hidden relative">
                      <div className="flex justify-between items-start mb-8">
                        <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                          <SafeIcon icon={FiUser} className="text-3xl" />
                        </div>
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedStudent(child); 
                            setSessionForReview(null);
                            setShowReviewModal(true); 
                          }} 
                          className="p-3 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-100 transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest"
                        >
                          <SafeIcon icon={FiStar} /> Rate Tutors
                        </button>
                      </div>
                      <h3 className="text-2xl font-black text-gray-900 mb-1">{child.studentName}</h3>
                      <p className="text-xs font-bold text-gray-400 mb-8 uppercase tracking-widest">Grade {child.grade}</p>
                      <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className="p-4 bg-gray-50 rounded-2xl">
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Classes</p>
                          <p className="text-lg font-black text-gray-900">{calculateTotalSessions(child.id)}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-2xl">
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Unpaid</p>
                          <p className="text-lg font-black text-rose-500">{unpaidCount}</p>
                        </div>
                      </div>
                      <div className="bg-indigo-600 rounded-2xl p-5 flex justify-between items-center text-white shadow-xl shadow-indigo-100">
                        <div>
                          <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest">Due Balance</p>
                          <p className="text-xl font-black">{formatCurrency(balance)}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                          <SafeIcon icon={FiArrowLeft} className="rotate-180" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <button onClick={() => setSelectedStudent(null)} className="mb-8 flex items-center gap-2 text-indigo-600 font-black text-xs uppercase tracking-widest hover:translate-x-[-4px] transition-transform">
                  <SafeIcon icon={FiArrowLeft} /> Back to Dashboard
                </button>
                <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white shadow-xl shadow-indigo-100">
                      <SafeIcon icon={FiUser} className="text-4xl" />
                    </div>
                    <div>
                      <h2 className="text-4xl font-black text-gray-900 tracking-tight">{selectedStudent.studentName}</h2>
                      <p className="text-gray-400 font-bold uppercase tracking-widest text-xs mt-1">Grade {selectedStudent.grade}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 w-full md:w-auto">
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Outstanding</p>
                      <p className="text-4xl font-black text-rose-500">{formatCurrency(calculateRemainingBalance(selectedStudent.id))}</p>
                    </div>
                    <button onClick={() => setShowPaymentModal(true)} className="flex-1 md:flex-none px-10 py-5 bg-indigo-600 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all"> 
                      Pay Now 
                    </button>
                  </div>
                </div>
                <SessionList studentId={selectedStudent.id} />
              </motion.div>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100">
            <h2 className="text-2xl font-black text-gray-900 mb-6 tracking-tight">Payment Ledger</h2>
            <TransactionHistory parentId={parentRecord?.id} />
          </div>
        )}

        {activeTab === 'comms' && <CommunicationLog />}

        <AnimatePresence>
          {showPaymentModal && selectedStudent && (
            <PaymentModal student={selectedStudent} onClose={() => setShowPaymentModal(false)} />
          )}
          {showReviewModal && selectedStudent && (
            <ReviewModal 
              student={selectedStudent} 
              session={sessionForReview} 
              onClose={() => {
                setShowReviewModal(false);
                setSessionForReview(null);
              }} 
            />
          )}
          {viewingReport && (
            <ViewReportModal report={viewingReport} onClose={() => setViewingReport(null)} />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ParentPortal;